import type { paths } from "@/api-schema";
import { alphartisApiAxios } from "@/config/axios";
import { useQuery } from "@tanstack/react-query";
import { useDebounce } from "@uidotdev/usehooks";
import { handleOrderableParameters } from "../_helpers/handle-parameters";

export const USER_APP_ROLE_RESOURCE = "user" as const;
export const USER_APP_ROLE_OPERATION = "app-role" as const;
export const USER_APP_ROLE_API_PATH = `/v1/${USER_APP_ROLE_RESOURCE}/{uuid}/${USER_APP_ROLE_OPERATION}` as const;

export type UserAppRoleResponse =
  paths[typeof USER_APP_ROLE_API_PATH]["get"]["responses"][200]["content"]["application/json"];
export type UserAppRoleParams = paths[typeof USER_APP_ROLE_API_PATH]["get"]["parameters"]["query"];

export const useUserAppRoleQuery = (
  uuid: string,
  search?: string,
  enabled: boolean = true,
  additionalParams?: Omit<UserAppRoleParams, "search">,
) => {
  const debouncedSearchString = useDebounce(search, 300);

  // Process the parameters to convert orderBy array to string format
  const processedParams = additionalParams ? handleOrderableParameters(additionalParams) : {};

  return useQuery({
    queryKey: [
      USER_APP_ROLE_RESOURCE,
      uuid,
      USER_APP_ROLE_OPERATION,
      debouncedSearchString,
      processedParams.page,
      processedParams.pageSize,
      processedParams.orderBy,
      processedParams.extend,
      processedParams.select,
    ],
    queryFn: async () => {
      const response = await alphartisApiAxios.get<UserAppRoleResponse>(
        `/v1/${USER_APP_ROLE_RESOURCE}/${uuid}/${USER_APP_ROLE_OPERATION}`,
        {
          params: {
            search: debouncedSearchString,
            ...processedParams,
          },
        },
      );

      return response.data;
    },
    enabled: enabled && !!uuid,
  });
};
